import { useState, useCallback, useRef } from 'react';
import { env } from '@/config/env';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

type SignupStep = 'welcome' | 'products' | 'email' | 'loading' | 'reveal' | 'magic-link' | 'creating' | 'complete' | 'profile-unlock';

export interface RubyPublicContext {
  type: 'landing' | 'auth';
  pageInfo?: string;
  signupStep?: SignupStep;
  email?: string;
  breachCount?: number;
  entriesEarned?: number;
}

interface UseRubyPublicChatOptions {
  context: RubyPublicContext;
}

function generateId() {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function parseQuickReplies(content: string): { displayText: string; quickReplies: string[] } {
  let displayText = content;
  let quickReplies: string[] = [];

  const quickReplyMatch = content.match(/\[QUICK_REPLIES:\s*([^\]]+)\]/);
  if (quickReplyMatch) {
    const repliesStr = quickReplyMatch[1];
    quickReplies = repliesStr.split(',').map(r => r.trim().replace(/^["']|["']$/g, ''));
    displayText = displayText.replace(/\[QUICK_REPLIES:[^\]]+\]/g, '').trim();
  }

  return { displayText, quickReplies };
}

const STEP_QUICK_REPLIES: Record<SignupStep, string[]> = {
  welcome: ['What is Reklaim?', 'Is it free?'],
  products: ["What's the jackpot?", 'Can I change later?'],
  email: ['Is my email safe?', 'What happens next?'],
  loading: [],
  reveal: ['What are these breaches?', 'How do entries work?'],
  'magic-link': ['Where is my link?', 'Is my data safe?'],
  creating: [],
  complete: ['What can I do now?', 'Show me my entries'],
  'profile-unlock': ['What questions will you ask?', 'Can I skip this?'],
};

const STEP_GREETINGS: Record<SignupStep, string> = {
  welcome: "Hi there! 👋 I'm Ruby. Need help getting started?",
  products: "Great choice! Most people select all three – you can always adjust later.",
  email: "Your email stays 100% private. We use it to scan for breaches and personalize your experience.",
  loading: "Scanning your data...",
  reveal: "Wow! Let me help you understand what we found.",
  'magic-link': 'Check your inbox—we sent a link. Look in spam if you don\'t see it.',
  creating: "Creating your account...",
  complete: "You're all set! 🎉 Welcome to Reklaim!",
  'profile-unlock': "Answer a few quick questions to unlock more entries! You can skip anytime.",
};

// Page-specific greetings for landing pages (no API call needed)
const PAGE_GREETINGS: Record<string, string> = {
  home: `Hey! 👋 I'm Ruby. Here's what you can do with Reklaim:

[CARD:jackpot]

[CARD:breach]

[CARD:protect]

Tap any card to get started, or ask me anything!`,
  earn: "Hi! 👋 I'm Ruby. Want to learn how to earn rewards with Reklaim?",
  protect: "Hi! 👋 I'm Ruby. Questions about data protection?",
  b2b: "Hi! 👋 I'm Ruby. Interested in Reklaim for your business?",
  breaches: "Hi! 👋 I'm Ruby. Want to understand the latest data breaches?",
  'breach-article': "Hi! 👋 I'm Ruby. Have questions about this breach?",
  predictions: "Hi! 👋 I'm Ruby. Want to learn how predictions work?",
  investors: "Hi! 👋 I'm Ruby. Looking for investor information?",
  help: "Hi! 👋 I'm Ruby. How can I help you today?",
  campaign: "Hi! 👋 I'm Ruby. Ready to start winning?",
};

const PAGE_QUICK_REPLIES: Record<string, string[]> = {
  home: ['How do I win the jackpot?', 'Remove me from data brokers', 'What do brands know about me?'],
  earn: ['How do predictions work?', 'What are jackpots?', 'How do I get entries?'],
  protect: ['What is Reklaim Protect?', 'How do you find breaches?', 'How much does it cost?'],
  b2b: ['What data do you offer?', 'How is it privacy-compliant?', 'Pricing options?'],
  breaches: ['How do I protect myself?', 'What should I do?', 'Get alerts for breaches'],
  'breach-article': ['Am I affected?', 'What should I do?', 'How can I protect myself?'],
  predictions: ['How do I stake?', 'What can I win?', 'How are winners chosen?'],
  investors: ['Company overview', 'Financial performance', 'Leadership team'],
  help: ['Getting started', 'My earnings', 'Account settings'],
  campaign: ['What is Reklaim?', 'How do I win?', 'Is it really free?'],
};

export function useRubyPublicChat({ context }: UseRubyPublicChatOptions) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasStartedRef = useRef(false);
  const hasGreetedRef = useRef(false);

  const processStream = useCallback(async (response: Response) => {
    const reader = response.body?.getReader();
    if (!reader) throw new Error('No reader available');

    const decoder = new TextDecoder();
    let textBuffer = '';
    let fullContent = '';
    const assistantId = generateId();

    setMessages(prev => [...prev, { id: assistantId, role: 'assistant', content: '' }]);

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              fullContent += content;
              const { displayText } = parseQuickReplies(fullContent);
              setMessages(prev =>
                prev.map(m => m.id === assistantId ? { ...m, content: displayText } : m)
              );
            }
          } catch {
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }

      // Final parse
      const { displayText, quickReplies: newReplies } = parseQuickReplies(fullContent);
      setMessages(prev =>
        prev.map(m => m.id === assistantId ? { ...m, content: displayText } : m)
      );
      
      // Use parsed quick replies or fall back to context-specific ones
      if (newReplies.length > 0) {
        setQuickReplies(newReplies);
      } else if (context.signupStep) {
        setQuickReplies(STEP_QUICK_REPLIES[context.signupStep] || []);
      } else if (context.pageInfo) {
        setQuickReplies(PAGE_QUICK_REPLIES[context.pageInfo] || []);
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        throw e;
      }
    }
  }, [context.signupStep, context.pageInfo]);

  const getEndpoint = useCallback(() => {
    if (context.type === 'auth') {
      return `${env.supabaseUrl}/functions/v1/ruby-signup-helper`;
    }
    return `${env.supabaseUrl}/functions/v1/ruby-public-help`;
  }, [context.type]);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isLoading) return;

    setError(null);
    setQuickReplies([]);

    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: text.trim(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      abortControllerRef.current = new AbortController();

      // Build messages for API - include hint that greeting already happened
      const allMessages = [...messages, userMessage].map(m => ({
        role: m.role,
        content: m.content,
      }));

      // Add system hint if this is the first user message after greeting
      const isFirstUserMessage = messages.filter(m => m.role === 'user').length === 0;

      const body = context.type === 'auth' 
        ? {
            messages: allMessages,
            context: {
              currentStep: context.signupStep,
              email: context.email,
              breachCount: context.breachCount,
              entriesEarned: context.entriesEarned,
            },
            alreadyGreeted: hasGreetedRef.current,
          }
        : {
            messages: allMessages,
            pageInfo: context.pageInfo,
            alreadyGreeted: hasGreetedRef.current,
          };

      const response = await fetch(getEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': env.supabaseKey,
        },
        body: JSON.stringify(body),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get response');
      }

      await processStream(response);
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        console.error('Ruby public chat error:', e);
        setError(e instanceof Error ? e.message : 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, context, getEndpoint, processStream]);

  const startConversation = useCallback(async () => {
    if (hasStartedRef.current || messages.length > 0 || isLoading) return;
    hasStartedRef.current = true;
    hasGreetedRef.current = true;

    // For auth context, show step-specific greeting immediately (no API call)
    if (context.type === 'auth' && context.signupStep) {
      const greeting = STEP_GREETINGS[context.signupStep];
      setMessages([{
        id: generateId(),
        role: 'assistant',
        content: greeting,
      }]);
      setQuickReplies(STEP_QUICK_REPLIES[context.signupStep] || []);
      return;
    }

    // For landing pages, show page-specific greeting immediately (no API call)
    const pageInfo = context.pageInfo || 'home';
    const greeting = PAGE_GREETINGS[pageInfo] || PAGE_GREETINGS.home;
    
    setMessages([{
      id: generateId(),
      role: 'assistant',
      content: greeting,
    }]);
    setQuickReplies(PAGE_QUICK_REPLIES[pageInfo] || PAGE_QUICK_REPLIES.home);
  }, [messages.length, isLoading, context]);

  const selectQuickReply = useCallback((reply: string) => {
    sendMessage(reply);
  }, [sendMessage]);

  const resetConversation = useCallback(() => {
    setMessages([]);
    setQuickReplies([]);
    setError(null);
    hasStartedRef.current = false;
    hasGreetedRef.current = false;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    messages,
    isLoading,
    quickReplies,
    error,
    sendMessage,
    startConversation,
    selectQuickReply,
    resetConversation,
    hasStarted: hasStartedRef.current,
  };
}
